import {
  IconChevronLeft,
  IconChevronRight,
  IconMinusVertical,
  IconMoon,
  IconScubaMask,
  IconSun,
} from '@tabler/icons-react';
import { AvailabilityDetail } from '../../../gql/graphql';
import {
  formatDateRange,
  getMonthName,
} from '../../../common/utilities/dateFormatter';
import { useEffect, useState } from 'react';
import { formatPrice } from '../../../common/utilities/priceFormatter';

interface Props {
  monthId: number;
  year: number;
  availabilityDetails: AvailabilityDetail[];
  setSelectedDetail: (availabilityDetail: AvailabilityDetail | null) => void;
  setShowResetModal: (showResetModal: boolean) => void;
  setShowItineraryModal: (showItineraryModal: boolean) => void;
  setSelectedItinerary: (itinerary: AvailabilityDetail | null) => void;
  selectedDetail: AvailabilityDetail | null;
  tripId: number;
}

const BoatTripComponent = (props: Props) => {
  const [currentMonth, setCurrentMonth] = useState(props.monthId);
  const [currentYear, setCurrentYear] = useState(props.year);
  const [isDetailHidden, setIsDetailHidden] = useState(false);

  const today = new Date();
  const realMonth = today.getMonth() + 1;
  const realYear = today.getFullYear();

  useEffect(() => {
    if (props.tripId !== 0 && !isDetailHidden) {
      props.setSelectedDetail(
        props.availabilityDetails.find(
          availabilityDetail => availabilityDetail.tripId === props.tripId,
        ) || null,
      );
    }
  }, [props.tripId, props.availabilityDetails, props, isDetailHidden]);

  const increaseMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleShowItinerary = (
    availabilityDetail: AvailabilityDetail | null,
  ) => {
    props.setSelectedItinerary(availabilityDetail);
    props.setShowItineraryModal(true);
  };

  const decreaseMonth = () => {
    // Prevent decreasing below the real world (today's) month
    if (currentYear === realYear && currentMonth === realMonth) {
      return;
    }
    if (currentMonth === 1) {
      const nextMonth = 12;
      const nextYear = currentYear - 1;
      // If the new state would fall before today's month/year, don't update
      if (
        nextYear < realYear ||
        (nextYear === realYear && nextMonth < realMonth)
      ) {
        return;
      }
      setCurrentMonth(nextMonth);
      setCurrentYear(nextYear);
    } else {
      const nextMonth = currentMonth - 1;
      // If the new state would be before today, don't update
      if (
        currentYear < realYear ||
        (currentYear === realYear && nextMonth < realMonth)
      ) {
        return;
      }
      setCurrentMonth(nextMonth);
    }
  };

  const isDecreaseDisabled =
    currentYear === realYear && currentMonth === realMonth;

  const filteredAvailabilityDetails = props.availabilityDetails.filter(
    availabilityDetail => {
      const embarkationDate = new Date(availabilityDetail.embarkationDate);
      const embarkationMonth = embarkationDate.getMonth() + 1; // Adding 1 to match your monthId values
      const embarkationYear = embarkationDate.getFullYear();

      return (
        embarkationMonth === currentMonth && embarkationYear === currentYear
      );
    },
  );

  const showDetail = (availabilityDetail: AvailabilityDetail) => {
    props.setSelectedDetail(availabilityDetail);
  };

  const hideDetail = () => {
    props.setShowResetModal(true);

    setIsDetailHidden(true);
  };

  return (
    <div className="mt-8">
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <IconScubaMask size={24} />
          <span className="text-lg font-semibold leading-normal text-neutral-800 md:text-2xl">
            Upcoming trips
          </span>
        </div>
        <span className="text-base font-normal leading-normal text-neutral-500">
          Discover your next diving expedition: choose a diving trip below.
        </span>
      </div>
      {props.selectedDetail === null && (
        <div className="mt-4 flex justify-center items-center gap-4 self-stretch">
          <button
            className="flex p-2 items-start gap-2 rounded-full border border-neutral-50 bg-white disabled:text-neutral-100"
            onClick={decreaseMonth}
            disabled={isDecreaseDisabled}
            aria-label="Previous month"
          >
            <IconChevronLeft size={20} />
          </button>
          <span className="text-base font-semibold leading-normal w-36 text-center">
            {getMonthName(currentMonth)} {currentYear}
          </span>
          <button
            className="flex p-2 items-start gap-2 rounded-full border border-neutral-50 bg-white"
            onClick={increaseMonth}
            aria-label="Next month"
          >
            <IconChevronRight size={20} />
          </button>
        </div>
      )}
      <div className="mt-4">
        {props.selectedDetail === null &&
          filteredAvailabilityDetails.length === 0 && (
            <div
              className="flex flex-col items-start gap-2 rounded-2xl bg-neutral-20 px-4 py-6"
              data-testid="boat-trips-empty"
            >
              <span className="text-base font-semibold leading-normal text-neutral-800">
                {props.availabilityDetails.length === 0
                  ? 'No upcoming trips available'
                  : 'No trips this month'}
              </span>
              <span className="text-sm font-normal leading-normal text-neutral-500">
                {props.availabilityDetails.length === 0
                  ? 'This boat has no scheduled itineraries right now. Check back later or try another boat.'
                  : 'Try another month to find available diving trips.'}
              </span>
            </div>
          )}
        {props.selectedDetail === null &&
          filteredAvailabilityDetails.map((availabilityDetail, index) => {
            const isSoldOut = (availabilityDetail.totalAvailability ?? 0) === 0;
            return (
              <button
                key={index}
                onClick={() => {
                  if (!isSoldOut) {
                    showDetail(availabilityDetail);
                  }
                }}
                disabled={isSoldOut}
                className={`flex py-4 items-center gap-4 self-stretch border-b border-neutral-100 md:w-full ${
                  isSoldOut ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <div className="flex flex-col items-start gap-2 flex-grow flex-shrink-0 md:hidden">
                  <span className="text-neutral-800 text-base font-bold leading-normal">
                    {formatDateRange(
                      availabilityDetail.embarkationDate,
                      availabilityDetail.disEmbarkationDate,
                    )}
                  </span>
                  <span className="text-neutral-800 text-base font-bold leading-normal">
                    {availabilityDetail.routeName}
                  </span>
                  <div className="flex items-center content-start gap-[0.5rem] self-stretch flex-wrap text-neutral-500">
                    <IconSun size={16} />
                    <span className="text-sm font-normal">
                      {availabilityDetail.numberOfDay}D
                    </span>
                    <IconMinusVertical size={18} />
                    <IconMoon size={16} />
                    <span className="text-sm font-normal">
                      {availabilityDetail.numberOfNight}N
                    </span>
                    <IconMinusVertical size={18} />
                    <IconScubaMask size={16} />
                    <span className="text-sm font-normal">
                      {availabilityDetail.numberOfDive} dives
                    </span>
                  </div>
                  <span
                    onClick={event => {
                      event.stopPropagation();
                      handleShowItinerary(availabilityDetail);
                    }}
                    className="text-sm font-semibold underline text-neutral-800 relative z-10"
                  >
                    Check itinerary
                  </span>
                </div>
                <div className="flex flex-col justify-center items-end gap-2 md:hidden">
                  <div className="flex flex-col justify-center items-end gap-1 md:flex-row md:items-center">
                    <span className="text-neutral-500 text-xs font-bold line-through">
                      {/* {formatPrice(availabilityDetail.price)} */}
                    </span>
                    <span className="text-neutral-800 text-base font-bold leading-normal">
                      {formatPrice(availabilityDetail.price, {
                        currencySymbol: availabilityDetail.currency || '',
                      })}
                    </span>
                  </div>
                  {isSoldOut ? (
                    <div className="bg-red-50 border border-red-200 rounded-2xl px-3 py-1 text-xs text-red-700 font-semibold">
                      Sold Out
                    </div>
                  ) : (
                    <div className="bg-neutral-50 rounded-2xl px-3 py-1 text-xs">
                      {availabilityDetail.totalAvailability} slots left
                    </div>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-6 self-stretch w-full">
                  <div className="flex items-start flex-grow">
                    <div className="flex w-36 py-4 pr-2 flex-col items-start gap-2">
                      <span className="text-neutral-800 text-base font-bold leading-normal">
                        {formatDateRange(
                          availabilityDetail.embarkationDate,
                          availabilityDetail.disEmbarkationDate,
                        )}
                      </span>
                      <div className="flex items-start gap-2">
                        <div className="flex items-center gap-1">
                          <IconSun size={16} />
                          <span className="text-sm font-normal">
                            {availabilityDetail.numberOfDay}D
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <IconMinusVertical size={18} />
                        </div>
                        <div className="flex items-center gap-1">
                          <IconMoon size={16} />
                          <span className="text-sm font-normal">
                            {availabilityDetail.numberOfNight}N
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex py-4 px-2 items-center gap-2 flex-grow">
                      <div className="flex flex-col items-start gap-2">
                        <span className="text-neutral-800 text-base font-bold leading-normal">
                          {availabilityDetail.routeName}
                        </span>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <IconScubaMask size={16} />
                            <span className="text-sm font-normal">
                              {availabilityDetail.numberOfDive} dives
                            </span>
                          </div>
                          <span
                            onClick={event => {
                              event.stopPropagation();
                              handleShowItinerary(availabilityDetail);
                            }}
                            className="text-sm underline text-neutral-500 relative z-10"
                          >
                            Check itinerary
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex py-4 px-2 flex-col justify-center items-end gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-500 text-xs font-bold line-through">
                          {/* {formatPrice(availabilityDetail.price)} */}
                        </span>
                        <span className="text-neutral-800 text-base font-bold leading-normal">
                          {formatPrice(availabilityDetail.price, {
                            currencySymbol: availabilityDetail.currency || '',
                          })}
                        </span>
                      </div>
                      {isSoldOut ? (
                        <div className="bg-red-50 border border-red-200 rounded-2xl px-3 py-1 text-xs text-red-700 font-semibold">
                          Sold Out
                        </div>
                      ) : (
                        <div className="bg-neutral-50 rounded-2xl px-3 py-1 text-xs">
                          {availabilityDetail.totalAvailability} slots left
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hidden md:flex">
                    <IconChevronRight size={20} />
                  </div>
                </div>
              </button>
            );
          })}
        {props.selectedDetail !== null && (
          <div className="flex p-4 flex-col justify-center items-start gap-6 self-stretch rounded-2xl bg-neutral-20">
            <button
              className="flex items-center gap-2"
              onClick={() => hideDetail()}
            >
              <IconChevronLeft size={20} />
              <span className="font-semibold">change my diving trip</span>
            </button>
            <div className="md:flex justify-between items-center w-full">
              <div className="flex items-center justify-between gap-4 self-stretch">
                <div className="flex flex-col items-start gap-2">
                  <span className="font-semibold">
                    {formatDateRange(
                      props.selectedDetail.embarkationDate,
                      props.selectedDetail.disEmbarkationDate,
                    )}
                  </span>
                  <div className="flex items-center gap-2 text-neutral-500">
                    <div className="flex items-center gap-1">
                      <IconSun size={16} />
                      <span>{props.selectedDetail.numberOfDay}D</span>
                    </div>
                    <IconMinusVertical size={18} />
                    <div className="flex items-center gap-1">
                      <IconMoon size={16} />
                      <span>{props.selectedDetail.numberOfNight}N</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="font-semibold">
                    {props.selectedDetail.routeName}
                  </span>
                  <div className="flex items-center gap-1 text-neutral-500">
                    <IconScubaMask size={16} />
                    <span>{props.selectedDetail.numberOfDive} dives</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleShowItinerary(props.selectedDetail)}
                className="text-sm font-semibold underline text-neutral-300 md:text-neutral-800 md:border md:border-neutral-800 md:hidden md:px-6 md:py-3 md:bg-neutral-00 md:rounded-lg md:text-base"
              >
                Check itinerary
              </button>
              <button
                onClick={() => handleShowItinerary(props.selectedDetail)}
                className="hidden font-semibold underline text-neutral-800 border border-neutral-800 px-6 py-3 bg-neutral-00 rounded-lg md:flex"
              >
                Check itinerary
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoatTripComponent;
